import { useCallback, useEffect, useState } from 'react';
import { Button, Flex, Paragraph, Input, Spinner } from 'theme-ui';

import { Svg } from '@snippets';

import { themed } from './AddToCart.theme';

export const QuantitySelector = themed(
  ({ theme, quantity, setQuantity, isMaxed, maxQty, qtyInCart }) => {
    const [updatedAt, setUpdatedAt] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);

    const disabled = quantity <= 1;

    const handleDecrement = useCallback(() => {
      if (disabled || isUpdating) return;
      setQuantity(quantity - 1);
      setUpdatedAt(new Date());
      setIsUpdating(true);
      setTimeout(() => setIsUpdating(false), 700);
    }, [disabled, quantity, isUpdating]);

    const handleIncrement = useCallback(() => {
      if (isMaxed || isUpdating) return;
      setQuantity(quantity + 1);
      setUpdatedAt(new Date());
      setIsUpdating(true);
      setTimeout(() => setIsUpdating(false), 700);
    }, [isMaxed, quantity, isUpdating]);

    const handleInputChange = useCallback(
      async (e) => {
        const typedQuantity = parseInt(e.target.value, 10);
        if (!typedQuantity) {
          setQuantity(quantity);
        } else if (typedQuantity < 1) {
          setQuantity(1);
        } else if (typedQuantity + qtyInCart > maxQty) {
          const remainder = maxQty - qtyInCart;
          setQuantity(remainder > 0 ? remainder : 1);
        } else {
          setQuantity(typedQuantity);
        }
        setUpdatedAt(new Date());
        setIsUpdating(true);
        setTimeout(() => setIsUpdating(false), 700);
      },
      [quantity, maxQty, qtyInCart]
    );

    return (
      <Flex data-comp={QuantitySelector.displayName} sx={theme.quantityWrapper}>
        <Button
          variant="buttons.plain"
          onClick={handleDecrement}
          disabled={disabled || isUpdating}
          sx={{
            ...theme.quantityButton,
            cursor: disabled || isUpdating ? 'default' : 'pointer',
            ':hover': {
              borderColor: disabled || isUpdating ? 'gray' : 'mediumGray',
            },
          }}
        >
          <Svg
            viewBox="0 0 48 48"
            alt="Decrease quantity"
            src="/svgs/minus.svg#minus"
            loading="lazy"
            sx={{
              ...theme.buttonIcon,
              color: disabled || isUpdating ? 'mediumDarkGray' : 'text',
            }}
          />
        </Button>

        <Flex
          sx={{
            variant: 'flex.row.center.center',
            flex: 1,
            mx: 2,
            position: 'relative',
            height: '100%',
            width: '44px',
          }}
        >
          <Paragraph
            as={Input}
            key={updatedAt}
            type="number"
            min="1"
            max={maxQty ? maxQty - qtyInCart : null}
            name="quantity"
            tabIndex="-1"
            aria-hidden="true"
            value={quantity}
            onBlur={handleInputChange}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleInputChange(e);
              }
            }}
            sx={{
              visibility: isUpdating ? 'hidden' : 'visible',
              fontSize: 4, // 16px
              fontFamily: 'body',
              width: '100%',
              height: '100%',
              textAlign: 'center',
              p: 0,
              bg: 'transparent',
              outline: 'none',
              borderStyle: 'none',
              ':focus, :hover': {
                outline: 'none !important',
              },
              '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                MozAppearance: 'none',
                margin: 0,
              },
              MozAppearance: 'textfield',
            }}
          />

          {isUpdating && (
            <Spinner
              sx={{
                width: '20px',
                color: 'mediumGray',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            />
          )}
        </Flex>

        <Button
          variant="buttons.plain"
          onClick={handleIncrement}
          disabled={isMaxed || isUpdating}
          sx={{
            ...theme.quantityButton,
            cursor: isMaxed || isUpdating ? 'default' : 'pointer',
            ':hover': {
              borderColor: isMaxed || isUpdating ? 'gray' : 'mediumGray',
            },
          }}
        >
          <Svg
            viewBox="0 0 48 48"
            alt="Increase quantity"
            src="/svgs/plus.svg#plus"
            loading="lazy"
            sx={{
              ...theme.buttonIcon,
              color: isMaxed || isUpdating ? 'mediumDarkGray' : 'text',
            }}
          />
        </Button>
      </Flex>
    );
  }
);

QuantitySelector.displayName = 'QuantitySelector';
